/**
 * Newsletter pop-up — appears on first visit (iter-51).
 *
 * Behavior :
 *   - Renders after ~5s when the user lands on the page for the first time
 *     in the session (gated via `sessionStorage`).
 *   - Closed by user → never reopens during the session.
 *   - Submitted successfully → marked as closed forever (localStorage).
 *
 * Backend : POST /api/newsletter-subscribers (existing endpoint).
 */
import { useEffect, useState } from "react";
import { X, Mail, Check } from "lucide-react";
import { trackEvent } from "../../lib/tracking";

const SESSION_KEY = "bbr_newsletter_popup_seen";
const PERMANENT_KEY = "bbr_newsletter_subscribed";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    try {
      if (window.sessionStorage.getItem(SESSION_KEY)) return;
      if (window.localStorage.getItem(PERMANENT_KEY)) return;
    } catch { /* ignore */ }
    const t = setTimeout(() => { if (!cancelled) setOpen(true); }, 4500);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  function dismiss() {
    try { window.sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
    // iter-66 — notify listeners (Featured Event popup) that the newsletter step is over
    try { window.dispatchEvent(new CustomEvent("bbr:newsletter-closed")); } catch { /* ignore */ }
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND}/api/newsletter-subscribers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          first_name: firstName.trim() || null,
          source: "landing_popup",
        }),
      });
      if (!res.ok) throw new Error("network");
      trackEvent("submit_lead", { channel: "newsletter", source: "popup" });
      setDone(true);
      try {
        window.localStorage.setItem(PERMANENT_KEY, new Date().toISOString());
        window.sessionStorage.setItem(SESSION_KEY, "1");
      } catch { /* ignore */ }
      setTimeout(() => {
        setOpen(false);
        try { window.dispatchEvent(new CustomEvent("bbr:newsletter-closed")); } catch { /* ignore */ }
      }, 1800);
    } catch {
      setError("Impossible d'enregistrer votre email. Réessayez dans un instant.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm"
      onClick={dismiss}
      data-testid="newsletter-popup"
    >
      <div
        className="relative bg-white text-[#0A0A0A] w-full max-w-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Fermer"
          className="absolute top-4 right-4 p-1.5 text-[#0A0A0A]/50 hover:text-[#0A0A0A] transition-colors"
          data-testid="newsletter-popup-close"
        >
          <X size={20} strokeWidth={1.5} />
        </button>

        <div className="flex flex-col items-center justify-center text-center px-8 sm:px-12 py-12 sm:py-14">
          {done ? (
            <div className="flex flex-col items-center" data-testid="newsletter-popup-success">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#B8922A]/10 text-[#B8922A] mb-5">
                <Check size={26} strokeWidth={1.5} />
              </div>
              <h3 className="font-serif font-light text-2xl mb-2">Merci !</h3>
              <p className="text-sm text-[#0A0A0A]/65">
                Vous êtes désormais inscrit·e à notre lettre.
              </p>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#B8922A]/40 text-[#B8922A] mb-6">
                <Mail size={18} strokeWidth={1.5} />
              </div>

              <h3 className="font-serif font-light text-3xl sm:text-4xl leading-tight tracking-wide mb-5">
                Life is Here
              </h3>

              <p className="text-sm text-[#0A0A0A]/70 leading-relaxed max-w-sm mb-8">
                Recevez nos rendez-vous signature, événements et offres confidentielles avant tout le monde.
              </p>

              <form onSubmit={onSubmit} className="w-full max-w-sm space-y-3" data-testid="newsletter-popup-form">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Prénom"
                  autoComplete="given-name"
                  className="w-full px-4 py-3 text-sm text-center border border-[#0A0A0A]/15 focus:border-[#B8922A] outline-none bg-white transition-colors"
                  data-testid="newsletter-popup-name"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  autoComplete="email"
                  required
                  className="w-full px-4 py-3 text-sm text-center border border-[#0A0A0A]/15 focus:border-[#B8922A] outline-none bg-white transition-colors"
                  data-testid="newsletter-popup-email"
                />
                {error && (
                  <p className="text-xs text-[#C24226]" data-testid="newsletter-popup-error">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full mt-3 inline-flex items-center justify-center gap-2 bg-[#B8922A] hover:bg-[#D4AF37] text-white py-3 text-[0.7rem] tracking-[0.3em] uppercase transition-colors disabled:opacity-60"
                  data-testid="newsletter-popup-submit"
                >
                  {submitting ? "Envoi…" : "S'inscrire"}
                </button>
                <p className="text-[0.65rem] text-[#0A0A0A]/45 pt-2">
                  Vous pouvez vous désabonner à tout moment.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
